import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from './ui/sonner';
import { Button } from './ui/button';
import {
  GraduationCap, BookOpen, Video, CheckCircle2, Circle, Lock,
  ChevronRight, ChevronDown, FileText, HelpCircle, ClipboardList,
  Download, LogOut, Menu, X, ArrowLeft
} from 'lucide-react';

// Use production URL on production, env var elsewhere
const API_URL = (typeof window !== 'undefined' && window.location.hostname === 'app.salesqualifyd.com')
  ? 'https://app.salesqualifyd.com/api'
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CourseViewer() {
  const { orgId, courseSlug, moduleSlug, chapterSlug } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeChapter, setActiveChapter] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [assignmentText, setAssignmentText] = useState('');
  const [assignmentUrl, setAssignmentUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('lms_token');
  const student = JSON.parse(localStorage.getItem('lms_student') || 'null');

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchCourse = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/lms/public/${orgId}/courses/${courseSlug}`, { headers });
      setCourse(res.data);
      // Auto-expand first module and select first chapter
      if (res.data.modules?.length > 0) {
        const firstMod = res.data.modules[0];
        setExpandedModules({ [firstMod.id]: true });
        if (firstMod.chapters?.length > 0 && !activeChapter) {
          setActiveChapter(firstMod.chapters[0]);
        }
      }
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('lms_token');
        navigate(`/${orgId}/courses/${courseSlug}/login`);
        return;
      }
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  }, [orgId, courseSlug, token]);

  useEffect(() => { fetchCourse(); }, [fetchCourse]);

  const toggleModule = (modId) => {
    setExpandedModules(prev => ({ ...prev, [modId]: !prev[modId] }));
  };

  const selectChapter = (ch) => {
    setActiveChapter(ch);
    setQuizAnswers({});
    setQuizResult(null);
    setAssignmentText('');
    setAssignmentUrl('');
    setMobileSidebarOpen(false);
  };

  const markComplete = async (chapterId, completed) => {
    if (!token || !course) return;
    try {
      await axios.post(`${API_URL}/lms/student/progress`, {
        chapter_id: chapterId,
        course_id: course.id,
        completed,
      }, { headers });
      fetchCourse();
      if (completed) toast.success('Chapter completed!');
    } catch {
      toast.error('Failed to update progress');
    }
  };

  const submitQuiz = async () => {
    if (!token || !activeChapter) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/lms/student/quiz-submit`, {
        chapter_id: activeChapter.id,
        course_id: course.id,
        answers: quizAnswers,
      }, { headers });
      setQuizResult(res.data);
      toast.success(`Score: ${res.data.score}/${res.data.total} (${res.data.percentage}%)`);
    } catch {
      toast.error('Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const submitAssignment = async () => {
    if (!token || !activeChapter) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/lms/student/assignment-submit`, {
        chapter_id: activeChapter.id,
        course_id: course.id,
        submission_text: assignmentText,
        submission_url: assignmentUrl,
      }, { headers });
      toast.success('Assignment submitted!');
      setAssignmentText('');
      setAssignmentUrl('');
    } catch {
      toast.error('Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('lms_token');
    localStorage.removeItem('lms_student');
    navigate(`/${orgId}/courses/${courseSlug}/login`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!course) return null;

  const brandColor = course.brand_color || '#2563eb';
  const progress = course.progress || { total: 0, completed: 0, percentage: 0 };
  const isEnrolled = course.is_enrolled;

  // Not enrolled — show course info
  if (!isEnrolled) {
    return (
      <div className="min-h-screen bg-zinc-50">
        <Toaster />
        <div className="max-w-3xl mx-auto py-12 px-6">
          <div className="bg-white border border-zinc-200 p-8">
            {course.brand_logo_url && <img src={course.brand_logo_url} alt="" className="h-10 mb-4" />}
            <h1 className="text-3xl font-bold text-zinc-900 mb-2">{course.title}</h1>
            <p className="text-zinc-600 mb-6">{course.description}</p>
            {course.instructor_name && (
              <div className="flex items-center gap-3 mb-6">
                {course.instructor_image_url && (
                  <img src={course.instructor_image_url} alt="" className="h-10 w-10 rounded-full object-cover" />
                )}
                <div>
                  <p className="text-sm font-medium text-zinc-800">{course.instructor_name}</p>
                  <p className="text-xs text-zinc-500">Instructor</p>
                </div>
              </div>
            )}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-zinc-700 mb-2">Course Content</h3>
              {course.modules?.map(mod => (
                <div key={mod.id} className="border-b border-zinc-100 py-2">
                  <p className="text-sm font-medium text-zinc-800">{mod.title}</p>
                  <p className="text-xs text-zinc-400">{mod.chapters?.length || 0} chapters</p>
                </div>
              ))}
            </div>
            <Button
              onClick={() => navigate(`/${orgId}/courses/${courseSlug}/login`)}
              className="w-full text-white"
              style={{ backgroundColor: brandColor }}
              data-testid="enroll-login-btn"
            >
              Log in to Access Course
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Enrolled — full course viewer
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Toaster />
      {/* Top bar */}
      <header className="h-14 border-b border-zinc-200 flex items-center justify-between px-4 bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <button className="md:hidden p-1" onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}>
            {mobileSidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          {course.brand_logo_url ? (
            <img src={course.brand_logo_url} alt="" className="h-7" />
          ) : (
            <GraduationCap className="h-6 w-6" style={{ color: brandColor }} />
          )}
          <span className="text-sm font-semibold text-zinc-800 hidden sm:inline">{course.title}</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-32 h-2 bg-zinc-200 overflow-hidden">
              <div className="h-full transition-all duration-500 ease-out" style={{ width: `${progress.percentage}%`, backgroundColor: brandColor }} />
            </div>
            <span className="text-xs font-mono text-zinc-500">{progress.percentage}%</span>
          </div>
          <span className="text-xs text-zinc-400 hidden sm:inline">{student?.email}</span>
          <button onClick={logout} className="p-1.5 hover:bg-zinc-100 rounded" data-testid="student-logout-btn">
            <LogOut className="h-4 w-4 text-zinc-500" />
          </button>
        </div>
      </header>

      <div className="flex flex-1 relative">
        {/* Sidebar */}
        <aside className={`
          ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
          fixed md:static z-20 top-14 left-0 h-[calc(100vh-3.5rem)]
          w-80 bg-zinc-50 border-r border-zinc-200 overflow-y-auto
          transition-transform md:transition-none
        `}>
          <div className="p-4">
            <h2 className="text-xs uppercase tracking-widest text-zinc-400 font-mono mb-3">Modules</h2>
            {course.modules?.map((mod, mi) => (
              <div key={mod.id} className="mb-1">
                <button
                  onClick={() => toggleModule(mod.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm font-medium rounded transition-colors ${
                    mod.is_locked ? 'text-zinc-400' : 'text-zinc-800 hover:bg-zinc-100'
                  }`}
                  data-testid={`module-nav-${mod.id}`}
                >
                  {mod.is_locked ? <Lock className="h-4 w-4 text-zinc-300 flex-shrink-0" /> : (
                    expandedModules[mod.id] ? <ChevronDown className="h-4 w-4 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 flex-shrink-0" />
                  )}
                  <span className="flex-1 truncate">{mod.title}</span>
                  {mod.is_locked && <span className="text-[10px] text-zinc-400 font-mono">LOCKED</span>}
                </button>
                {expandedModules[mod.id] && !mod.is_locked && (
                  <div className="ml-4 space-y-0.5">
                    {mod.chapters?.map(ch => (
                      <button
                        key={ch.id}
                        onClick={() => selectChapter(ch)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded transition-colors ${
                          activeChapter?.id === ch.id
                            ? 'bg-white border border-zinc-200 font-medium'
                            : 'text-zinc-600 hover:bg-zinc-100'
                        }`}
                        data-testid={`chapter-nav-${ch.id}`}
                      >
                        {ch.completed ? (
                          <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: brandColor }} />
                        ) : (
                          <Circle className="h-4 w-4 text-zinc-300 flex-shrink-0" />
                        )}
                        <span className="flex-1 truncate">{ch.title}</span>
                        <div className="flex gap-0.5">
                          {ch.has_video && <Video className="h-3 w-3 text-zinc-400" />}
                          {ch.has_quiz && <HelpCircle className="h-3 w-3 text-zinc-400" />}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          {activeChapter ? (
            <div className="max-w-4xl mx-auto p-6 md:p-10">
              <h2 className="text-2xl font-bold text-zinc-900 mb-6" data-testid="chapter-title">{activeChapter.title}</h2>

              {/* Video */}
              {activeChapter.video_embed_code && (
                <div className="aspect-video bg-black w-full mb-6 relative" data-testid="video-container">
                  <div dangerouslySetInnerHTML={{ __html: activeChapter.video_embed_code }} className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full" />
                </div>
              )}

              {/* Content */}
              {activeChapter.content_html && (
                <div
                  className="prose prose-zinc max-w-none mb-8"
                  dangerouslySetInnerHTML={{ __html: activeChapter.content_html }}
                  data-testid="chapter-content"
                />
              )}

              {/* Attachments */}
              {activeChapter.attachments?.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-sm font-semibold text-zinc-700 mb-2">Attachments</h3>
                  <div className="space-y-2">
                    {activeChapter.attachments.map((att, i) => (
                      <a
                        key={i}
                        href={att.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-2 border border-zinc-200 text-sm hover:bg-zinc-50 transition-colors"
                      >
                        <Download className="h-4 w-4 text-zinc-400" />
                        <span className="text-zinc-700">{att.name || att.url}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Quiz */}
              {activeChapter.quiz?.questions?.length > 0 && (
                <div className="mb-8 border border-zinc-200 p-6">
                  <h3 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2">
                    <HelpCircle className="h-5 w-5" style={{ color: brandColor }} /> Quiz
                  </h3>
                  {activeChapter.quiz.questions.map((q, qi) => (
                    <div key={qi} className="mb-4">
                      <p className="text-sm font-medium text-zinc-800 mb-2">{qi + 1}. {q.question}</p>
                      <div className="space-y-1 ml-4">
                        {q.options.map((opt, oi) => (
                          <label key={oi} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="radio"
                              name={`q-${qi}`}
                              checked={quizAnswers[String(qi)] === String(oi)}
                              onChange={() => setQuizAnswers({ ...quizAnswers, [String(qi)]: String(oi) })}
                              className="accent-red-700"
                              disabled={!!quizResult}
                            />
                            <span className={quizResult ? (
                              quizResult.answers?.[qi]?.is_correct && String(oi) === quizResult.answers[qi].student_answer ? 'text-green-700 font-medium' :
                              String(oi) === quizResult.answers?.[qi]?.correct_answer ? 'text-green-600' :
                              String(oi) === quizResult.answers?.[qi]?.student_answer ? 'text-red-500 line-through' : 'text-zinc-600'
                            ) : 'text-zinc-700'}>
                              {opt}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  {!quizResult ? (
                    <Button
                      onClick={submitQuiz}
                      disabled={submitting || Object.keys(quizAnswers).length < activeChapter.quiz.questions.length}
                      className="text-white mt-2"
                      style={{ backgroundColor: brandColor }}
                      data-testid="submit-quiz-btn"
                    >
                      {submitting ? 'Submitting...' : 'Submit Quiz'}
                    </Button>
                  ) : (
                    <div className="mt-4 p-3 border" style={{ borderColor: brandColor, backgroundColor: `${brandColor}10` }}>
                      <p className="font-semibold" style={{ color: brandColor }}>
                        Score: {quizResult.score}/{quizResult.total} ({quizResult.percentage}%)
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Assignment */}
              {activeChapter.assignment && (
                <div className="mb-8 border border-zinc-200 p-6">
                  <h3 className="text-lg font-semibold text-zinc-900 mb-2 flex items-center gap-2">
                    <ClipboardList className="h-5 w-5" style={{ color: brandColor }} /> Assignment
                  </h3>
                  <p className="text-sm text-zinc-600 mb-4">{activeChapter.assignment.description || activeChapter.assignment.title}</p>
                  <div className="space-y-3">
                    <textarea
                      className="w-full border border-zinc-300 p-3 text-sm min-h-[100px] resize-y focus:ring-2 focus:ring-blue-500"
                      placeholder="Your answer..."
                      value={assignmentText}
                      onChange={e => setAssignmentText(e.target.value)}
                      data-testid="assignment-text-input"
                    />
                    <input
                      type="url"
                      className="w-full border border-zinc-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      placeholder="Submission URL (optional)"
                      value={assignmentUrl}
                      onChange={e => setAssignmentUrl(e.target.value)}
                      data-testid="assignment-url-input"
                    />
                    <Button
                      onClick={submitAssignment}
                      disabled={submitting || (!assignmentText && !assignmentUrl)}
                      className="text-white"
                      style={{ backgroundColor: brandColor }}
                      data-testid="submit-assignment-btn"
                    >
                      {submitting ? 'Submitting...' : 'Submit Assignment'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Complete button */}
              <div className="flex items-center justify-between py-6 border-t border-zinc-200">
                <Button
                  onClick={() => markComplete(activeChapter.id, !activeChapter.completed)}
                  variant={activeChapter.completed ? 'outline' : 'default'}
                  className={activeChapter.completed ? 'border-green-300 text-green-700' : 'text-white'}
                  style={!activeChapter.completed ? { backgroundColor: brandColor } : {}}
                  data-testid="mark-complete-btn"
                >
                  {activeChapter.completed ? (
                    <><CheckCircle2 className="h-4 w-4 mr-1" /> Completed</>
                  ) : (
                    <><Circle className="h-4 w-4 mr-1" /> Mark as Complete</>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-zinc-400">
              <div className="text-center">
                <BookOpen className="h-12 w-12 mx-auto mb-3" />
                <p className="text-sm">Select a chapter to start learning</p>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
